import { API_BASE } from "@/lib/apiBase";
import { api } from "@/lib/apiClient";

export type VeranstaltungStatus = "neu" | "gesehen" | "ohne";
export type VeranstaltungFilter = "alle" | "neu" | "gesehen" | "mit_einsatz" | "ohne";

export interface VeranstaltungEinsatz {
  bereich: string;
  ort: string;
  kategorie: string;
  datum: string;
  aufgabe: string;
  abteilung: string;
  firma: string;
  seite: number | null;
  datei?: string | null;
}

export interface VeranstaltungAttachment {
  id: string;
  name: string;
  size?: number;
  contentType?: string;
  is_rp?: boolean;
  einsatz_count?: number;
  parse_error?: string;
}

export interface VeranstaltungMail {
  id: number;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  received_at: string | null;
  snippet: string | null;
  has_rp: boolean;
  attachments_summary: VeranstaltungAttachment[];
  event_title: string | null;
  event_date: string | null;
  event_version: string | null;
  rp_nummer: string | null;
  psp: string | null;
  venue: string | null;
  einsaetze: VeranstaltungEinsatz[];
  einsatz_count: number;
  status: VeranstaltungStatus;
  seen_at: string | null;
  seen_by: string | null;
}

export interface VeranstaltungCounts {
  total: number;
  neu: number;
  gesehen: number;
  mit_einsatz: number;
  ohne: number;
  offene_einsaetze: number;
}

export interface VeranstaltungListResponse {
  items: VeranstaltungMail[];
  counts: VeranstaltungCounts;
  mailbox: string;
  live_monitor: boolean;
  monitor_enabled: boolean;
  last_poll_at: string | null;
  last_error: string | null;
}

export interface VeranstaltungScanJob {
  job_id: string;
  status: "queued" | "running" | "completed" | "failed";
  total: number | null;
  processed: number;
  with_einsatz: number;
  without_einsatz: number;
  errors: number;
  progress_percent: number;
  truncated: boolean;
  message: string;
  error: string | null;
}

export const VERANSTALTUNG_SCAN_JOB_STORAGE_KEY = "veranstaltungScanJobId";

export async function fetchVeranstaltungUngelesenCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/veranstaltungen/ungelesen-count");
  return Number(data?.count || 0);
}

export function listVeranstaltungMails(status: VeranstaltungFilter = "alle") {
  const query = status === "alle" ? "" : `?status=${encodeURIComponent(status)}`;
  return api.get<VeranstaltungListResponse>(`/veranstaltungen/mails${query}`);
}

export function scanVeranstaltungMailbox(sinceDate: string) {
  return api.post<{ ok: boolean; job_id: string; mailbox: string; message: string }>(
    `/veranstaltungen/scan?since_date=${encodeURIComponent(sinceDate)}`,
    {},
  );
}

export function getVeranstaltungScanJob(jobId: string) {
  return api.get<VeranstaltungScanJob>(`/veranstaltungen/scan/${encodeURIComponent(jobId)}`);
}

export function reanalyzeVeranstaltungMail(id: number) {
  return api.post<VeranstaltungMail>(`/veranstaltungen/mails/${id}/reanalyze`, {});
}

export function markVeranstaltungMail(id: number, seen: boolean) {
  return api.post<VeranstaltungMail>(`/veranstaltungen/mails/${id}/gesehen`, { seen });
}

export async function openVeranstaltungAttachment(mailId: number, attachmentId: string) {
  const url = `${API_BASE}/veranstaltungen/mails/${mailId}/attachments/${encodeURIComponent(attachmentId)}`;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error("Das Regieprotokoll konnte nicht geöffnet werden.");
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank", "noopener");
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
